import { ProductFormulationDto, TaskDto } from '../../../api';
import { ExtendedTaskType, PRODUCT_APPROVAL_RENEWAL_REQUIRED } from '../../newsSection/taskSection/TaskSectionHelper';

export const isFormulationEditable = (formulationId: string, formulations: ProductFormulationDto[]) => {
    const formulationState = formulations.find((formulation) => formulation.id === formulationId)?.formulationState;
    return (
        formulationState === ProductFormulationDto.formulationState.NEW ||
        formulationState === ProductFormulationDto.formulationState.REWORK_NECESSARY
    );
};

/**
 *  Determines if the "NoRowsOverlay" button should be disabled:
 *  Disabled when there are no formulations (hasFormulations = false)
 *  application usage mode is not BEAM-Full
 *  Enabled only if there are formulations AND the beam is full
 */
export const isNoRowsOverlayButtonDisabled = (formulations: ProductFormulationDto[], isFullBeam: boolean) => {
    return !hasFormulations(formulations) || !isFullBeam;
};
export const hasFormulations = (formulations: ProductFormulationDto[]) => {
    return formulations.length > 0;
};

export const hasSingleOpenFormulation = (formulations: ProductFormulationDto[]) => {
    const openFormulations = formulations.filter(
        (formulation) =>
            formulation.formulationState === ProductFormulationDto.formulationState.NEW ||
            formulation.formulationState === ProductFormulationDto.formulationState.REQUESTED
    );
    return openFormulations.length === 1;
};
/**
 * Checks if the annual report is available for the given formulation state.
 * The annual report is considered available if the formulation state is one of the following:
 * - APPROVED
 * - ANNUAL_REPORT_DUE
 * - ANNUAL_REPORT_OVERDUE
 * - ANNUAL_REPORT_NOT_EXTENDED
 *
 * @param formulationState - The formulation state to check.
 * @returns True if the annual report is available, false otherwise.
 */
export const isAnnualReportAvailable = (formulationState?: ProductFormulationDto.formulationState) => {
    return (
        formulationState === ProductFormulationDto.formulationState.APPROVED ||
        formulationState === ProductFormulationDto.formulationState.ANNUAL_REPORT_DUE ||
        formulationState === ProductFormulationDto.formulationState.ANNUAL_REPORT_OVERDUE ||
        formulationState === ProductFormulationDto.formulationState.ANNUAL_REPORT_NOT_EXTENDED ||
        formulationState === ProductFormulationDto.formulationState.RENEWAL_REQUIRED ||
        formulationState === ProductFormulationDto.formulationState.RENEWAL_APPROVAL_REQUIRED
    );
};

/**
 *  Determines if the "Action Required" column should be shown in the formulations table.
 * @param formulations
 * @returns
 */

export const showActionRequiredColumnByFormulations = (formulations?: ProductFormulationDto[]) => {
    return formulations?.some(
        (formulation) =>
            formulation.formulationState === ProductFormulationDto.formulationState.ANNUAL_REPORT_DUE ||
            formulation.formulationState === ProductFormulationDto.formulationState.ANNUAL_REPORT_OVERDUE ||
            formulation.formulationState === ProductFormulationDto.formulationState.RENEWAL_REQUIRED ||
            formulation.formulationState === ProductFormulationDto.formulationState.RENEWAL_APPROVAL_REQUIRED ||
            formulation.formulationState === ProductFormulationDto.formulationState.REWORK_NECESSARY
    );
};

/**
 * Gets the count of tasks for a specific task type.
 * @param tasksType - The type of tasks to count.
 * @param tasks - The array of TaskDto objects.
 * @returns The count of tasks for the specified type.
 */

export const getTaskCount = (tasksType: ExtendedTaskType, tasks?: TaskDto[]) => {
    if (!tasks) {
        return 0;
    }
    if (tasksType === TaskDto.type.ANY_ACTION_REQUIRED) {
        const total = tasks.reduce((acc, task) => acc + (task.count ?? 0), 0);
        return total;
    }
    const filteredTasks = tasks.filter((task) => task.type === tasksType);
    return filteredTasks[0]?.count ?? 0;
};
/**
 * Gets the list of product task types excluding COMPANY_INVOICE_OPEN and ANY_ACTION_REQUIRED.
 * @returns An array of TaskDto.type values excluding COMPANY_INVOICE_OPEN and ANY_ACTION_REQUIRED.
 */
export const getProductTaskTypes = (): ExtendedTaskType[] => {
    const taskTypes: ExtendedTaskType[] = Object.values(TaskDto.type).filter(
        (type) => type !== TaskDto.type.COMPANY_INVOICE_OPEN
    );
    taskTypes.push(PRODUCT_APPROVAL_RENEWAL_REQUIRED);
    return taskTypes.sort((a, b) => a.localeCompare(b));
};

export const getAllProductsTasks = (tasks: TaskDto[]): number => {
    return tasks
        .filter((task) => task.type !== TaskDto.type.COMPANY_INVOICE_OPEN)
        .reduce((acc, task) => acc + (task.count ?? 0), 0);
};
